import axios from '../axios';
import { fireAnalytics } from '../utils/firebase';
import { getSessionId, getDeviceCategory } from '../utils/analyticsHelpers.ts';

export interface LLMAnalysisResult {
  actionId: string | null;
  confidence: number;
  response: string;
  guideMode?: boolean;
  needsData?: boolean;
  data?: any;
  total?: number;
  noResultsMessage?: string;
  quotationType?: string;
  workVisibility?: string;
  isPublic?: boolean;
  collectedData?: Record<string, any>;
}

export interface ConversationMessage {
  role: 'user' | 'assistant';
  content: string;
  actionId?: string | null;
}

export interface AnalyzeWithLLMOptions {
  activeAgent?: string;
  handoffContext?: { userIntent: string };
  collectedData?: Record<string, any>;
  imageUploaded?: boolean;
  readyToExecute?: boolean;
  image?: Blob | File;
}

const getAuthHeaders = () => {
  const authToken = localStorage.getItem("access-Token-Edify");
  return { headers: { Authorization: `Bearer ${authToken}` } };
};

const getUserId = (): string => {
  try {
    const state = JSON.parse(localStorage.getItem('persist:general') || '{}');
    const generalState = JSON.parse(state.general || '{}');
    return generalState.userData?.id || '';
  } catch (e) {
    return '';
  }
};

export const analyzeWithLLM = async (
  query: string, 
  history?: ConversationMessage[],
  options?: AnalyzeWithLLMOptions
): Promise<LLMAnalysisResult> => {
  try {
    const payloadHistory = history || [];
    const formData = new FormData();
    formData.append('message', query);
    formData.append('history', JSON.stringify(payloadHistory));
    formData.append('activeAgent', options?.activeAgent || 'general');
    if (options?.handoffContext) {
      formData.append('handoffContext', JSON.stringify(options.handoffContext));
    }
    if (options?.collectedData) {
      formData.append('collectedData', JSON.stringify(options.collectedData));
    }
    const imageUploaded = options?.imageUploaded ?? false;
    formData.append('image_uploaded', imageUploaded ? 'true' : 'false');
    const readyToExecute = options?.readyToExecute ?? false;
    formData.append('readyToExecute', readyToExecute ? 'true' : 'false');
    if (options?.image) {
      formData.append('image', options.image, 'image.jpg');
    }

    const response = await axios.post<{
      message: string; 
      data: LLMAnalysisResult;
    }>(
      '/chatbot/message',
      formData,
      getAuthHeaders()
    );
    
    const responseData = response.data.data;
    
    if (!responseData) {
      throw new Error('Respuesta del servidor en formato inesperado');
    }
    
    const userId = getUserId();
    fireAnalytics("agent_message_sent", {
      edify_user_id: userId,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "agent",
      action: "message_sent",
      query_length: query.length,
      action_id: responseData.actionId || null,
      confidence: responseData.confidence || null,
      guide_mode: responseData.guideMode || false,
      has_data: !!responseData.data,
      needs_data: responseData.needsData || false,
      history_length: payloadHistory.length
    });
    
    return responseData;
  } catch (error) {
    const userId = getUserId();
    fireAnalytics("agent_message_error", {
      edify_user_id: userId,
      session_id: getSessionId(),
      device_category: getDeviceCategory(),
      category: "agent",
      action: "message_error",
      query_length: query.length,
      history_length: history?.length || 0,
      error_type: error instanceof Error ? error.message : "unknown_error"
    });
    
    throw new Error('Error conectando con el servicio de análisis');
  }
};

